import { useEffect, useRef, useState } from "react";
import styles from "./LoadingDots.module.scss";
import { BRAND_NAME } from "../../../constant/common";

// Master switch for the sibling-overlay loading indicator. Flip to `false` to
// disable the centered phrase + wavy dots entirely — the component renders
// nothing and the consumer (App.tsx) effectively sees no overlay. Co-located
// with the component so toggling is one edit.
export const SHOW_LOADING_DOTS = false;

// Travel-themed phrases cycled through with a typewriter effect. Each cycle
// picks a random phrase, never repeating the previous one back-to-back.
const LOADING_PHRASES = [
  "Crafting your perfect escape",
  "Loading adventure",
  "Your journey is loading",
  "Where to next? Finding out",
  "Almost there, traveler",
  `Welcome to ${BRAND_NAME}`,
  "Let's Begin Your Journey",
];

// Typewriter cycle timings — total 1812ms per message:
//   TYPE  560ms — chars appear progressively (per-char step = TYPE_MS / length)
//   HOLD  692ms — full text visible
//   ERASE 560ms — chars removed from the end
// Next cycle starts immediately with a fresh random phrase.
// Dismiss is honored ONLY at cycle boundaries (after erase completes), never
// mid-cycle — so the user always sees a smooth full-cycle typewriter before
// the indicator disappears. Fast devices: ~1 cycle then dismiss. Slow
// devices: loop through phrases until init resolves.
const TYPE_MS = 560;
const HOLD_MS = 692;
const ERASE_MS = 560;

const MAX_CYCLES = 5;

type LoadingDotsProps = {
  // True when backend init is complete and the home page is ready to show.
  // Checked only at cycle boundaries — never mid-cycle. Pass `true` from the
  // start to dismiss after exactly one cycle; flip later to dismiss after
  // the current cycle completes. Default `false` means the component cycles
  // indefinitely until the parent un-mounts it (e.g. Suspense fallback case
  // where the chunk-load completion is the natural dismiss signal).
  isPageReady?: boolean;
  showText?: boolean;
  // Called once when (a) at least one full typewriter cycle has completed
  // AND (b) `isPageReady` is true at that cycle boundary. Consumer should
  // respond by un-mounting this component. Omit to keep cycling until
  // the parent un-mounts.
  onDismiss?: () => void;
};

// Centered loading indicator with three wave-bouncing dots. Rendered as a
// sibling overlay in App.tsx on top of the home page during the post-fox-loader
// window where backend init (appConfig, countries, prompts) is still resolving.
// Container is transparent + `pointer-events: none` so the home page underneath
// stays visible and interactive.
export default function LoadingDots({
  isPageReady = false,
  onDismiss,
  showText = true,
}: LoadingDotsProps): JSX.Element {
  const [text, setText] = useState("");
  // Refs hold the latest prop values so the timeout chain (closures over the
  // mount-time props) sees current state on each tick — without restarting
  // the cycle every time props change.
  const isPageReadyRef = useRef(isPageReady);
  const onDismissRef = useRef(onDismiss);

  useEffect(() => {
    isPageReadyRef.current = isPageReady;
  }, [isPageReady]);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    let timeoutId: number | undefined;
    let cancelled = false;
    let lastIndex = -1;

    let cycleCount = 0;

    const pickNextIndex = (): number => {
      // Avoid repeating the previous phrase so consecutive cycles feel
      // distinct. With 5 phrases the rejection loop runs at most twice.
      let idx: number;
      do {
        idx = Math.floor(Math.random() * LOADING_PHRASES.length);
      } while (idx === lastIndex && LOADING_PHRASES.length > 1);
      lastIndex = idx;
      return idx;
    };

    // Single state machine driving TYPE → HOLD → ERASE → next phrase.
    // Recursive setTimeout chain (not setInterval) so each step's delay can
    // differ — per-char step during type/erase, full HOLD_MS at the apex.
    const tick = (phrase: string, charIndex: number, mode: "type" | "erase"): void => {
      if (cancelled) return;
      setText(phrase.substring(0, charIndex));

      if (mode === "type") {
        if (charIndex < phrase.length) {
          const perChar = TYPE_MS / phrase.length;
          timeoutId = window.setTimeout(() => tick(phrase, charIndex + 1, "type"), perChar);
        } else {
          // Type complete → HOLD, then start erasing from the last char.
          timeoutId = window.setTimeout(() => tick(phrase, charIndex - 1, "erase"), HOLD_MS);
        }
      } else {
        // mode === "erase"
        if (charIndex > 0) {
          const perChar = ERASE_MS / phrase.length;
          timeoutId = window.setTimeout(() => tick(phrase, charIndex - 1, "erase"), perChar);
        } else {
          cycleCount += 1;
          const reachedMax = cycleCount >= MAX_CYCLES;
          if ((isPageReadyRef.current || reachedMax) && onDismissRef.current) {
            cancelled = true;
            onDismissRef.current();
            return;
          }
          const nextPhrase = LOADING_PHRASES[pickNextIndex()];
          tick(nextPhrase, 0, "type");
        }
      }
    };

    // Kick off the first cycle.
    const first = LOADING_PHRASES[pickNextIndex()];
    tick(first, 0, "type");

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  if (!SHOW_LOADING_DOTS) return <></>;

  return (
    <div className={styles.container} role="status">
      <div className={styles.content}>
        {showText && (
          <span className={styles.label} aria-hidden="true">
            {text}
          </span>
        )}
        <span className={styles.dots} aria-hidden="true">
          <span className={styles.dot} />
          <span className={styles.dot} />
          <span className={styles.dot} />
        </span>
      </div>
      <span
        aria-live="polite"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        Loading, please wait
      </span>
    </div>
  );
}
